/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  X,
  ArrowRight,
  Sparkles,
  Wallet,
  Landmark,
  Gift,
  Palmtree,
  ClipboardList,
  Clock,
  TrendingUp,
  Scale,
  Building2,
  FileText,
  CreditCard,
  Receipt,
  Building,
  FileCheck,
} from "lucide-react";

import CalculadoraSueldoNeto from "../Pages/Herramientas/SueldoNeto";
import CalculadoraCTS from "../Pages/Herramientas/CTS";
import CalculadoraGratificacion from "../Pages/Herramientas/Gratificacion";
import CalculadoraVacaciones from "../Pages/Herramientas/Vacaciones";
import CalculadoraLiquidacion from "../Pages/Herramientas/Liquidacion";
import CalculadoraHorasExtras from "../Pages/Herramientas/HorasExtras";
import CalculadoraUtilidades from "../Pages/Herramientas/Utilidades";
import CalculadoraIndemnizacion from "../Pages/Herramientas/Indemnizacion";
import CalculadoraAFP from "../Pages/Herramientas/AFP";
import CalculadoraONP from "../Pages/Herramientas/ONP";
import CalculadoraCuartaCategoria from "../Pages/Herramientas/CuartaCategoria";
import CalculadoraIGV from "../Pages/Herramientas/IGV";
import CalculadoraDevolucionSUNAT from "../Pages/Herramientas/DevolucionSUNAT";
import ConstanciaTrabajo from "../Pages/Herramientas/ConstanciaTrabajo";

const FeaturedCard = ({ item, onClick }) => {
  const ref = useRef(null);
  const Icon = item.icono;

  const handleMouseMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--x", `${((e.clientX - rect.left) / rect.width) * 100}%`);
    el.style.setProperty("--y", `${((e.clientY - rect.top) / rect.height) * 100}%`);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onClick={onClick}
      initial={{ opacity: 0, scale: 0.94 }}
      whileInView={{ opacity: 1, scale: 1 }}
      transition={{ type: "spring", stiffness: 120, damping: 16 }}
      viewport={{ once: true }}
      className="relative overflow-hidden bg-gray-900 border border-gray-800 cursor-pointer rounded-2xl group"
      style={{ "--x": "50%", "--y": "50%" }}
    >
      <div
        className="absolute inset-0 transition-opacity duration-300 opacity-0 pointer-events-none group-hover:opacity-100"
        style={{
          background:
            "radial-gradient(320px circle at var(--x) var(--y), rgba(147,51,234,0.20), transparent 65%)",
        }}
      />
      <div className="absolute inset-0 transition-colors duration-300 border border-transparent pointer-events-none rounded-2xl group-hover:border-blue-500/30" />

      <div className="relative z-10 flex flex-col justify-between h-full p-7 min-h-[220px]">
        <div className="flex items-start justify-between">
          <div className="flex items-center justify-center w-12 h-12 border rounded-xl bg-white/5 border-white/10">
            <Icon className="w-6 h-6 text-blue-400" strokeWidth={1.75} />
          </div>
          <span className="flex items-center gap-1 text-[11px] font-mono tracking-widest text-purple-400/80 uppercase">
            <Sparkles className="w-3 h-3" />
            top
          </span>
        </div>

        <div>
          <h3 className="mb-2 text-xl font-semibold text-white">
            {item.nombre}
          </h3>
          <p className="text-sm leading-relaxed text-gray-400">
            {item.descripcion}
          </p>
        </div>

        <div className="flex items-center gap-2 mt-6 text-sm font-medium text-blue-400 transition-transform duration-300 group-hover:translate-x-1">
          Calcular ahora
          <ArrowRight className="w-4 h-4" />
        </div>
      </div>
    </motion.div>
  );
};

const TicketRow = ({ item, index, onClick }) => {
  const Icon = item.icono;

  return (
    <motion.button
      onClick={onClick}
      initial={{ opacity: 0, x: -16 }}
      whileInView={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4, ease: "easeOut", delay: (index % 6) * 0.04 }}
      viewport={{ once: true, margin: "-20px" }}
      className="relative flex items-center w-full gap-4 px-5 py-4 overflow-hidden text-left transition-colors duration-200 border-b border-gray-800/70 group hover:bg-gray-900/60"
    >
      <span className="absolute top-0 left-0 w-0.5 h-full origin-top scale-y-0 bg-gradient-to-b from-blue-500 to-purple-500 transition-transform duration-300 group-hover:scale-y-100" />

      <span className="font-mono text-xs tracking-wider text-gray-600 transition-colors tabular-nums shrink-0 group-hover:text-blue-400/80">
        N.º {String(item.id).padStart(2, "0")}
      </span>

      <div className="flex items-center justify-center transition-colors border rounded-lg w-9 h-9 shrink-0 bg-white/5 border-white/10 group-hover:border-blue-500/30">
        <Icon className="w-4 h-4 text-gray-400 transition-colors group-hover:text-blue-400" strokeWidth={1.75} />
      </div>

      <div className="min-w-0 grow">
        <p className="text-sm font-medium text-white truncate">{item.nombre}</p>
        <p className="text-xs text-gray-500 truncate">{item.descripcion}</p>
      </div>

      <ArrowRight className="w-4 h-4 text-gray-700 transition-all duration-300 shrink-0 group-hover:text-blue-400 group-hover:translate-x-1" />
    </motion.button>
  );
};

export default function Herramientas() {
  const [busqueda, setBusqueda] = useState("");
  const [herramientaActiva, setHerramientaActiva] = useState(null);

  const herramientas = [
    {
      id: 1,
      icono: Wallet,
      nombre: "Calculadora de Sueldo Neto",
      descripcion: "Calcula tu sueldo neto según descuentos.",
      componente: CalculadoraSueldoNeto,
      destacada: true,
    },
    {
      id: 2,
      icono: Landmark,
      nombre: "Calculadora CTS",
      descripcion: "Calcula tu Compensación por Tiempo de Servicios.",
      componente: CalculadoraCTS,
      destacada: true,
    },
    {
      id: 3,
      icono: Gift,
      nombre: "Calculadora Gratificación",
      descripcion: "Calcula tu gratificación de julio o diciembre.",
      componente: CalculadoraGratificacion,
      destacada: true,
    },
    {
      id: 4,
      icono: Palmtree,
      nombre: "Calculadora Vacaciones",
      descripcion: "Calcula vacaciones truncas o completas.",
      componente: CalculadoraVacaciones,
    },
    {
      id: 5,
      icono: ClipboardList,
      nombre: "Calculadora Liquidación",
      descripcion: "Calcula tu liquidación laboral.",
      componente: CalculadoraLiquidacion,
    },
    {
      id: 6,
      icono: Clock,
      nombre: "Calculadora Horas Extras",
      descripcion: "Calcula el pago de horas extras.",
      componente: CalculadoraHorasExtras,
    },
    {
      id: 7,
      icono: TrendingUp,
      nombre: "Calculadora Utilidades",
      descripcion: "Calcula utilidades laborales.",
      componente: CalculadoraUtilidades,
    },
    {
      id: 8,
      icono: Scale,
      nombre: "Calculadora Indemnización",
      descripcion: "Calcula indemnización por despido.",
      componente: CalculadoraIndemnizacion,
    },
    {
      id: 9,
      icono: Building2,
      nombre: "Calculadora AFP",
      descripcion: "Calcula descuentos AFP.",
      componente: CalculadoraAFP,
    },
    {
      id: 10,
      icono: FileText,
      nombre: "Calculadora ONP",
      descripcion: "Calcula descuentos ONP.",
      componente: CalculadoraONP,
    },
    {
      id: 11,
      icono: CreditCard,
      nombre: "Calculadora 4ta Categoría",
      descripcion: "Calcula renta de cuarta categoría.",
      componente: CalculadoraCuartaCategoria,
    },
    {
      id: 12,
      icono: Receipt,
      nombre: "Calculadora IGV",
      descripcion: "Calcula IGV incluido o sin IGV.",
      componente: CalculadoraIGV,
    },
    {
      id: 13,
      icono: Building,
      nombre: "Calculadora Devolución SUNAT",
      descripcion: "Calcula devolución estimada.",
      componente: CalculadoraDevolucionSUNAT,
    },
    {
      id: 14,
      icono: FileCheck,
      nombre: "Constancia de Trabajo",
      descripcion: "Genera una constancia de trabajo.",
      componente: ConstanciaTrabajo,
    },
  ];

  const herramientasFiltradas = useMemo(() => {
    return herramientas.filter((item) =>
      item.nombre.toLowerCase().includes(busqueda.toLowerCase())
    );
  }, [busqueda]);

  const buscando = busqueda.trim().length > 0;
  const destacadas = herramientas.filter((h) => h.destacada);
  const resto = herramientasFiltradas.filter((h) => !h.destacada);

  const ComponenteActivo = herramientaActiva?.componente;

  return (
    <>
      <section className="relative min-h-screen py-20 overflow-hidden bg-black">
        <div className="absolute top-0 left-1/2 w-[600px] h-[600px] -translate-x-1/2 -translate-y-1/3 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] translate-x-1/3 translate-y-1/3 bg-purple-600/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 max-w-6xl px-5 mx-auto">
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ type: "spring", stiffness: 100 }}
            viewport={{ once: true }}
            className="mb-12 text-center"
          >
            <h1 className="mb-4 text-4xl font-bold text-transparent md:text-5xl bg-clip-text bg-gradient-to-r from-blue-400 to-purple-600">
              Herramientas Laborales
            </h1>
            <p className="max-w-2xl mx-auto text-lg text-gray-400">
              Calculadoras laborales y financieras para trabajadores,
              independientes y empresas.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            viewport={{ once: true }}
            className="max-w-xl mx-auto mb-14"
          >
            <div className="relative">
              <Search className="absolute w-5 h-5 text-gray-500 -translate-y-1/2 left-4 top-1/2" />
              <input
                type="text"
                placeholder="Buscar herramienta..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="w-full py-3.5 pl-12 pr-4 text-white placeholder-gray-500 bg-gray-900 border border-gray-800 outline-none rounded-xl focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 transition-all"
              />
            </div>
          </motion.div>

          {!buscando && (
            <div className="mb-4">
              <p className="mb-5 font-mono text-xs tracking-widest text-gray-600 uppercase">
                Más usadas
              </p>
              <div className="grid gap-5 mb-14 md:grid-cols-3">
                {destacadas.map((item) => (
                  <FeaturedCard
                    key={item.id}
                    item={item}
                    onClick={() => setHerramientaActiva(item)}
                  />
                ))}
              </div>
            </div>
          )}

          <p className="mb-2 font-mono text-xs tracking-widest text-gray-600 uppercase">
            {buscando ? "Resultados" : "Todas las herramientas"}
          </p>

          {(buscando ? herramientasFiltradas : resto).length > 0 ? (
            <div className="overflow-hidden border border-gray-800 rounded-2xl bg-gray-900/30">
              {(buscando ? herramientasFiltradas : resto).map((item, index) => (
                <TicketRow
                  key={item.id}
                  item={item}
                  index={index}
                  onClick={() => setHerramientaActiva(item)}
                />
              ))}
            </div>
          ) : (
            <p className="py-10 text-center text-gray-500">
              No se encontró ninguna herramienta para "{busqueda}".
            </p>
          )}
        </div>
      </section>

      <AnimatePresence>
        {herramientaActiva && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setHerramientaActiva(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", stiffness: 260, damping: 24 }}
              className="relative w-full max-w-3xl max-h-[85vh] overflow-y-auto bg-gray-900 border border-gray-800 shadow-2xl rounded-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-5 border-b border-gray-800 bg-gray-900/95 backdrop-blur">
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center rounded-lg w-11 h-11 bg-gradient-to-br from-blue-600/30 to-purple-600/30">
                    {herramientaActiva.icono && (
                      <herramientaActiva.icono
                        className="w-5 h-5 text-blue-400"
                        strokeWidth={1.75}
                      />
                    )}
                  </div>
                  <div>
                    <p className="font-mono text-[11px] tracking-widest text-gray-500 uppercase">
                      Herramienta
                    </p>
                    <h2 className="text-xl font-bold text-white">
                      {herramientaActiva.nombre}
                    </h2>
                  </div>
                </div>

                <button
                  onClick={() => setHerramientaActiva(null)}
                  className="flex items-center justify-center text-gray-400 transition-colors rounded-lg w-9 h-9 hover:text-white hover:bg-gray-800"
                  aria-label="Cerrar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-8">
                {ComponenteActivo && <ComponenteActivo />}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}